import { useMemo, useState } from 'react';
import {
  ArrowRight,
  BarChart3,
  ClipboardCheck,
  Database,
  FileSpreadsheet,
  FileText,
  Library,
  LineChart,
  MessageSquare,
  PieChart,
  Search,
  ShieldAlert,
  ShieldCheck,
  Terminal,
  Workflow,
} from 'lucide-react';
import OrcaLogo from './OrcaLogo';
import BrainstormIcon from './AiCopilot/BrainstormIcon';
import Login from './Login';
import LegalPrivacy from './LegalPrivacy';

const FEATURE_COPY = {
  'ask-data': {
    title: 'Ask Data',
    blurb:
      'Ask in English. Orca chooses BIP, HCM REST, or composed SQL and returns a live table you can export.',
    example: 'Show me a list of all active employees',
  },
  'sql-studio': {
    title: 'SQL Studio',
    blurb:
      'Generate OTBI Logical SQL or native Oracle SQL, edit it, then run a preview and export CSV, Excel, or PDF.',
    example: 'Headcount by department for the last 12 months',
  },
  report: {
    title: 'Report Copilot',
    blurb:
      'Describe the report you need. Orca finds the BIP or OTBI object, suggests parameters, and helps you run it.',
    example: 'Show overtime hours by department for the last pay period',
  },
  'reports-catalog': {
    title: 'Reports Catalog',
    blurb:
      'Index the tenant BIP and OTBI catalog, search by name or kind, then run a report with the right layout and params.',
    example: 'Quick scan the catalog, then run a BIP report inline',
  },
  'hdl-errors': {
    title: 'HDL Errors',
    blurb:
      'Preview HDL load and DAT line errors from curated SQL recipes so implementation issues are visible without BI Publisher.',
    example: 'Show HDL load errors with full message detail',
  },
  connections: {
    title: 'Connections',
    blurb:
      'Save per-user Fusion pods with encrypted credentials. Test, activate, and keep tenant URLs out of Heroku config.',
    example: 'Add https://acme.fa.us6.oraclecloud.com and activate',
  },
  bpm: {
    title: 'Approval Copilot',
    blurb:
      'Describe an approval policy in plain English. Orca drafts the rule, conditions, and approver path.',
    example: 'If salary change is over 15%, route to Manager then HR VP',
  },
  'approval-designer': {
    title: 'Approval Designer',
    blurb:
      'Design, preview, and simulate BPM approval paths against historic Fusion transactions before you deploy.',
    example: 'Simulate the path for person number 12345',
  },
  security: {
    title: 'Security Copilot',
    blurb:
      'Ask why a user cannot see a page or row. Orca walks roles, privileges, data security, and AOR to the smallest fix.',
    example: "Why can't Jane see the Compensation Workbench?",
  },
  users: {
    title: 'User Lookup',
    blurb: 'Look up a worker and inspect assigned roles, privileges, and security access without opening Security Console.',
    example: 'Find Mark Smith and list effective roles',
  },
  uar: {
    title: 'Access Review',
    blurb: 'Generate user-access review campaigns from a natural-language scope, then track approve or revoke decisions.',
    example: "Review everyone with Compensation Manager or Payroll in the US",
  },
  'audit-alerts': {
    title: 'Audit Alerts',
    blurb: 'Watch Fusion audit activity for unusual grants, privileged changes, and other security anomalies.',
    example: 'Flag unexpected role grants on privileged users',
  },
  'sod-rules': {
    title: 'SoD Rules',
    blurb: 'Define segregation-of-duties rules and scan user assignments for conflicting access.',
    example: 'Payroll setup must not combine with payroll disbursement',
  },
};

const FEATURE_ICONS = {
  'ask-data': BrainstormIcon,
  'sql-studio': Terminal,
  report: MessageSquare,
  'reports-catalog': Library,
  'hdl-errors': ShieldAlert,
  connections: Database,
  bpm: Workflow,
  'approval-designer': Workflow,
  security: ShieldCheck,
  users: Search,
  uar: ClipboardCheck,
  'audit-alerts': ShieldAlert,
  'sod-rules': ShieldCheck,
};

const PRODUCT_HERO = {
  reports: {
    eyebrow: 'Oracle Cloud HCM · Reports',
    headline: 'Ask Oracle HCM in plain English.',
    highlight: 'Get the report, the SQL, or the table.',
    sub:
      'Ask Data, SQL Studio, and Report Copilot find, compose, and export BIP and OTBI work as charts, Excel, or PDF.',
  },
  security: {
    eyebrow: 'Oracle Cloud HCM · Approvals & Security',
    headline: 'See the grant. Draft the rule.',
    highlight: 'Review the access.',
    sub:
      'Orca AI Approvals & Security explains access issues, drafts BPM approval rules, and runs access reviews on the live Fusion tenant.',
  },
};

function shouldOpenLogin() {
  try {
    const params = new URLSearchParams(window.location.search);
    return Boolean(params.get('login') === '1' || params.get('loginError'));
  } catch {
    return false;
  }
}

function FeatureIcon({ id, className }) {
  const Icon = FEATURE_ICONS[id] || Database;
  return <Icon className={className} />;
}

const WATERMARK_ICONS = [
  { Icon: BarChart3, className: 'top-6 left-[6%]' },
  { Icon: PieChart, className: 'top-8 left-[28%]' },
  { Icon: LineChart, className: 'top-5 right-[30%]' },
  { Icon: FileSpreadsheet, className: 'top-7 right-[8%]' },
  { Icon: FileText, className: 'top-16 left-[16%]' },
  { Icon: Library, className: 'top-14 right-[18%]' },
  { Icon: BarChart3, className: 'bottom-8 left-[8%]' },
  { Icon: PieChart, className: 'bottom-10 left-[26%]' },
  { Icon: LineChart, className: 'bottom-7 right-[28%]' },
  { Icon: FileSpreadsheet, className: 'bottom-9 right-[7%]' },
  { Icon: FileText, className: 'bottom-16 left-[18%]' },
  { Icon: Terminal, className: 'bottom-14 right-[16%]' },
];

function ReportTypesBackdrop() {
  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden>
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-25"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL || ''}/reports-landing-bg.jpg)`,
        }}
      />
      {WATERMARK_ICONS.map(({ Icon, className }, i) => (
        <Icon
          key={`${className}-${i}`}
          className={`absolute h-16 w-16 text-stone-400 ${className}`}
          strokeWidth={1.25}
          style={{ opacity: 0.12 }}
        />
      ))}
    </div>
  );
}

function BrandMarks({ size = 'header' }) {
  const pub = process.env.PUBLIC_URL || '';
  const slpClass = size === 'footer' ? 'h-20 w-20' : 'h-16 w-16';
  const oracleClass = size === 'footer' ? 'h-16 max-w-[280px]' : 'h-14 max-w-[240px]';
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="landing-brand-mark inline-flex items-center justify-center rounded-lg px-3 py-2 ring-1 ring-stone-300">
        <img src={`${pub}/slp-logo.png`} alt="SLP" className={`${slpClass} object-contain`} />
      </div>
      <div className="landing-brand-mark inline-flex items-center justify-center rounded-lg px-4 py-2 ring-1 ring-stone-300">
        <img
          src={`${pub}/oracle-service-partner.png`}
          alt="Oracle Service Partner"
          className={`${oracleClass} w-auto object-contain`}
        />
      </div>
    </div>
  );
}

export default function Landing({ product, onSignedIn }) {
  const [showLogin, setShowLogin] = useState(shouldOpenLogin);
  const [legalDoc, setLegalDoc] = useState(null);
  const views = useMemo(() => [...(product?.copilotViews || []), ...(product?.adminViews || [])], [product]);
  const hero = PRODUCT_HERO[product?.id] || PRODUCT_HERO.reports;

  function openLogin() {
    setShowLogin(true);
  }

  function closeLogin() {
    setShowLogin(false);
    try {
      const url = new URL(window.location.href);
      ['login', 'loginError', 'signedOut', 'switch'].forEach((key) => url.searchParams.delete(key));
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    } catch {
      // ignore
    }
  }

  if (legalDoc) {
    return <LegalPrivacy docId={legalDoc} onBack={() => setLegalDoc(null)} />;
  }

  return (
    <div className="relative min-h-screen bg-[#1a1612] text-stone-100">
      <ReportTypesBackdrop />
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#1a1612]/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <a href="#top" className="flex items-center gap-2.5">
            <OrcaLogo className="h-9 w-9" />
            <span>
              <span className="block text-sm font-semibold text-white">{product?.name || 'Orca AI'}</span>
              <span className="block text-xs text-slate-300">{product?.tagline || 'Reports'}</span>
            </span>
          </a>
          <nav className="hidden items-center gap-5 text-sm text-slate-200 md:flex">
            <a href="#features" className="hover:text-white">
              Features
            </a>
            <BrandMarks size="header" />
          </nav>
          <button
            type="button"
            onClick={openLogin}
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-amber-500"
          >
            Sign in
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        <div className="flex justify-center border-t border-white/10 px-4 py-3 md:hidden">
          <BrandMarks size="header" />
        </div>
      </header>

      <main id="top" className="relative z-10">
        <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:py-28">
          <div className="mx-auto max-w-xl rounded-2xl border border-white/10 bg-[#1a1612]/70 p-6 shadow-2xl backdrop-blur-sm sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-300">{hero.eyebrow}</p>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight text-white sm:text-5xl">
              {hero.headline}
              <span className="mt-2 block text-amber-200">{hero.highlight}</span>
            </h1>
            <p className="mt-5 text-base leading-relaxed text-stone-300">{hero.sub}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={openLogin}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-amber-500"
              >
                Sign in
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>

        <section id="features" className="border-t border-white/10 bg-[#1a1612]/70 backdrop-blur-sm">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-300">Product</p>
            <h2 className="mt-2 text-3xl font-semibold text-orca-heading">Everything in this app</h2>
            <p className="mt-3 max-w-2xl text-sm text-orca-muted">
              Same sidebar you get after sign-in — copilots first, then admin tools. Each card is a live
              workspace, not a brochure feature.
            </p>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {views.map((view) => {
                const copy = FEATURE_COPY[view.id] || {
                  title: view.label,
                  blurb: `Open ${view.label} after you sign in.`,
                  example: '',
                };
                return (
                  <article
                    key={view.id}
                    className="flex flex-col rounded-2xl border border-orca bg-orca-panel p-5 shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-orca-raised text-amber-400">
                        <FeatureIcon id={view.id} className="h-5 w-5" />
                      </span>
                      <h3 className="text-base font-semibold text-orca-heading">{copy.title}</h3>
                    </div>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-orca-muted">{copy.blurb}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <footer className="relative z-10 border-t border-white/10 bg-[#1a1612]/90">
        <div className="mx-auto flex max-w-6xl flex-col gap-5 px-4 py-8 sm:px-6 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <OrcaLogo className="h-8 w-8" />
            <BrandMarks size="footer" />
            <p className="max-w-md text-[11px] leading-relaxed text-slate-400">
              {product?.name || 'Orca AI'} {product?.tagline || 'Reports'} is an independent Oracle
              Cloud HCM service partner offering. Oracle and Java are registered trademarks of Oracle
              and/or its affiliates.
            </p>
          </div>
          <nav className="flex flex-wrap items-center gap-4 text-xs font-medium text-slate-300">
            <button type="button" onClick={() => setLegalDoc('legal')} className="hover:text-white">
              Legal
            </button>
            <button type="button" onClick={() => setLegalDoc('privacy')} className="hover:text-white">
              Privacy
            </button>
            <span className="text-slate-500">© {new Date().getFullYear()}</span>
          </nav>
        </div>
      </footer>

      {showLogin && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/70">
          <Login product={product} onSuccess={onSignedIn} onBack={closeLogin} />
        </div>
      )}
    </div>
  );
}
